'use client';
import React, { useState, useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from '../../api';
import {
  Form,
  Select,
  InputNumber,
  Button,
  message,
  Card,
  Row,
  Col,
  Spin,
  Table,
  Modal,
  Typography,
  Space,
  Input
} from 'antd';
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import JsBarcode from 'jsbarcode';

const { Title } = Typography;


const API_BASE = `${BASE_URL}/api/`;
const api = axios.create({ baseURL: API_BASE });


// ──────────────────────────────────────────────
// Barcode as data URL image (more reliable for PDF capture)
// ──────────────────────────────────────────────
const BarcodeItem = ({ value }) => {
  const [src, setSrc] = useState('');

  useEffect(() => {
    if (!value) return;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(svg, value, {
      format: 'CODE128',
      width: 2,
      height: 60,
      displayValue: true,
      fontSize: 16,
      margin: 10
    });

    const svgString = new XMLSerializer().serializeToString(svg);
    const encoded = encodeURIComponent(svgString);
    setSrc(`data:image/svg+xml;charset=utf-8,${encoded}`);
  }, [value]);

  return src ? (
    <img
      src={src}
      alt="barcode"
      style={{ width: '100%', height: 'auto', minHeight: 80 }}
      crossOrigin="anonymous"
    />
  ) : null;
};

api.interceptors.request.use(config => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const Booking = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    pickRequests: [],
    consignees: [],
    areas: [],
    pincodes: []
  });
  const [status, setStatus] = useState('loading');
  const [listModal, setListModal] = useState(false);
  const [barcodeModal, setBarcodeModal] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [onlineList, setOnlineList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const printLabelsRef = useRef();

  const filteredList = onlineList.filter((item) => {
    const docket = item.docketNumber?.toLowerCase() || '';
    const consignee = item.consigneeid?.companyName?.toLowerCase() || '';
    const search = searchText.toLowerCase();
    return docket.includes(search) || consignee.includes(search);
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pr, cons, area, pin] = await Promise.all([
        api.get('/pickrequests'),
        api.get('/master/customer'),
        api.get('/master/area'),
        api.get('/master/pincode')
      ]);

      setData({
        pickRequests: pr.data.data || [],
        consignees: cons.data.data || [],
        areas: area.data.data || [],
        pincodes: pin.data.data || []
      });

      form.setFieldsValue({
        bookingdate: dayjs(),
        isoda: false,
        boxConfirmationModel: [{ length: 30, breadth: 30, height: 30, noofBox: 1 }]
      });

      updateTotals();
      setStatus('ready');
    } catch (err) {
      console.error(err);
      setStatus('error');
      toast.error('Failed to load master data');
    }
  };

  const isSuperAdmin = () => {
    const role = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
    return role.includes('super admin') || role.includes('superadmin') || role.includes('super_admin') || role === 'admin';
  };

  const handleDeleteBooking = async (record) => {
    if (!isSuperAdmin()) {
      toast.error('Only Super Admin can delete bookings');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete docket ${record.docketNumber || 'this booking'}?`)) {
      return;
    }

    try {
      const res = await api.delete(`onlinebookings/${record._id}`);
      if (res.data && (res.data.success || res.status === 200)) {
        toast.success('Booking deleted successfully');
        message.success('Booking deleted successfully');
        await loadOnlineBookings();
      } else {
        toast.error(res.data?.message || 'Failed to delete booking');
      }
    } catch (err) {
      console.error('Delete booking error:', err);
      toast.error(err.response?.data?.message || 'Failed to delete booking');
    }
  };

  const loadOnlineBookings = async () => {
    try {
      setListLoading(true);
      const res = await api.get('/onlinebookings');
      setOnlineList(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setListLoading(false);
    }
  };

  const updateTotals = () => {
    const values = form.getFieldsValue();
    const boxes = values.boxConfirmationModel || [];
    const actualWeight = Number(values.actweight) || 0;
    let totalBoxes = 0;
    let totalVol = 0;

    boxes.forEach(box => {
      if (box && typeof box === 'object') {
        const qty = Number(box.noofBox) || 0;
        const l = Number(box.length) || 0;
        const b = Number(box.breadth) || 0;
        const h = Number(box.height) || 0;
        totalBoxes += qty;
        if (l && b && h && qty && actualWeight > 0) {
          totalVol += ((l * b * h) / actualWeight) * qty;
        }
      }
    });

    form.setFieldsValue({
      noofbox: totalBoxes,
      volume: Number(totalVol.toFixed(4)),
      volweight: Math.round(totalVol * 5000)
    });
  };

  const handlePickRequestChange = id => {
    const pr = data.pickRequests.find(p => p._id === id);
    if (pr) {
      const matchedArea = data.areas.find(a => a.areaname?.toUpperCase() === pr.location?.toUpperCase());
      const matchedPin = data.pincodes.find(p => p.pincode === pr.pickuppin);

      form.setFieldsValue({
        locationid: matchedArea?._id || null,
        topincode: matchedPin?._id || null,
        actweight: pr.actweight || 0,
        noofbox: pr.noofbox || 1
      });

      updateTotals();
      toast.success(`Details loaded for ${pr.pickrequestnumber}`);
    }
  };

  const onFinish = async values => {
    setSubmitting(true);
    try {
      const response = await api.post('/onlinebookings', {
        ...values,
        bookingdate: values.bookingdate?.toISOString() || new Date().toISOString()
      });

      const newBooking = response.data?.data?.booking;
      if (!newBooking || !newBooking.docketNumber) {
        toast.error('Booking created, but docket number missing');
        return;
      }

      toast.success('Booking Created Successfully');

      const count = newBooking.noofbox || 1;
      const codes = Array.from({ length: count }, (_, i) =>
        `${newBooking.docketNumber}-${(i + 1).toString().padStart(2, '0')}`
      );

      const pickupPR = data.pickRequests.find(p => p._id === values.pickrequestid);
      const deliveryArea = data.areas.find(a => a._id === values.locationid);
      const deliveryPin = data.pincodes.find(p => p._id === values.topincode);

      setSelectedBooking({
        ...newBooking,
        generatedCodes: codes,
        consigneeid: { companyName: values.consignee_name || newBooking.consigneeid?.companyName || 'N/A' },
        fromLocation: pickupPR
          ? `${pickupPR.location || 'N/A'} (${pickupPR.pickuppin || 'N/A'})`
          : 'N/A',
        toLocation: deliveryArea && deliveryPin
          ? `${deliveryArea.areaname || 'N/A'} (${deliveryPin.pincode || 'N/A'})`
          : 'N/A'
      });

      setBarcodeModal(true);

      form.resetFields();
      form.setFieldsValue({
        bookingdate: dayjs(),
        isoda: false,
        boxConfirmationModel: [{ length: 30, breadth: 30, height: 30, noofBox: 1 }]
      });
      updateTotals();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShowBarcodeFromList = record => {
    const count = record.noofbox || 1;
    const codes = Array.from({ length: count }, (_, i) =>
      `${record.docketNumber}-${(i + 1).toString().padStart(2, '0')}`
    );

    const consigneeObj = typeof record.consigneeid === 'object' && record.consigneeid ? record.consigneeid : {};
    const consigneeName = consigneeObj.companyName || consigneeObj.name || record.consigneeName || 'N/A';

    const fromLoc =
      record.pickrequestid?.location ||
      record.pickrequestid?.locationid?.areaname ||
      record.pickrequestid?.clientid?.station ||
      record.pickrequestid?.clientid?.branch?.branchname ||
      (record.fromLocation && record.fromLocation !== 'N/A' ? record.fromLocation : null) ||
      'N/A';
    const toLoc =
      record.locationid?.areaname ||
      record.topincode?.locationname ||
      record.topincode?.pincode ||
      (record.toLocation && record.toLocation !== 'N/A' ? record.toLocation : null) ||
      'N/A';

    setSelectedBooking({
      ...record,
      generatedCodes: codes,
      consigneeid: { ...consigneeObj, companyName: consigneeName },
      fromLocation: fromLoc,
      toLocation: toLoc
    });

    setListModal(false);
    setBarcodeModal(true);
  };

  const handleDownloadPDF = async () => {
    if (!selectedBooking?.generatedCodes?.length) {
      message.warning('No labels available to download');
      return;
    }

    message.loading('Preparing PDF...', 0);

    // Small delay to ensure barcodes are rendered
    await new Promise(resolve => setTimeout(resolve, 400));

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 10;
      let y = margin;

      for (let i = 0; i < selectedBooking.generatedCodes.length; i++) {
        const labelElement = document.getElementById(`label-${i}`);
        if (!labelElement) continue;

        const canvas = await html2canvas(labelElement, {
          scale: 2.8,              // good quality vs size balance
          useCORS: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) {
          pdf.addPage();
          y = margin;
        }

        pdf.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight);
        y += imgHeight + 10; // slightly larger gap
      }

      pdf.save(`Barcodes_${selectedBooking.docketNumber || 'booking'}_${dayjs().format('YYYYMMDD-HHmm')}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF');
    } finally {
      message.destroy();
    }
  };

  if (status === 'loading') {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
      <ToastContainer />
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Create Online Booking</Title>}
        extra={
          <Button type="primary" onClick={() => { setListModal(true); loadOnlineBookings(); }}>
            Online Booking List
          </Button>
        }
      >
        <Form form={form} onFinish={onFinish} layout="vertical" onValuesChange={updateTotals}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="pickrequestid" label="Pick Request" rules={[{ required: true }]}>
                <Select showSearch onChange={handlePickRequestChange} placeholder="Select Pick Request">
                  {data.pickRequests.map(x => (
                    <Select.Option key={x._id} value={x._id}>
                      {x.pickrequestnumber} - {x.location}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="consigneeid" label="Consignee" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select Consignee">
                  {data.consignees.map(x => (
                    <Select.Option key={x._id} value={x._id}>{x.companyName}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="topincode" label="Delivery Pincode" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select Pincode">
                  {data.pincodes.map(x => (
                    <Select.Option key={x._id} value={x._id}>
                      {x.pincode} - {x.locationname}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
              <Col span={12}>
              <Form.Item name="locationid" label="Delivery Area" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select Area">
                  {data.areas.map(x => (
                    <Select.Option key={x._id} value={x._id}>{x.areaname}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="actweight" label="Actual Weight (kg)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0.1} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="noofbox" label="Total Boxes">
                <InputNumber disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="volume" label="Volume">
                <InputNumber disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Card title="Box Dimensions" size="small" style={{ marginTop: 16 }}>
            <Form.List name="boxConfirmationModel">
              {(fields, { add, remove }) => (
                <Table
                  dataSource={fields}
                  pagination={false}
                  rowKey="key"
                  columns={[
                    { title: 'L (cm)', render: (_, field) => <Form.Item {...field} name={[field.name, 'length']} noStyle><InputNumber min={1} /></Form.Item> },
                    { title: 'B (cm)', render: (_, field) => <Form.Item {...field} name={[field.name, 'breadth']} noStyle><InputNumber min={1} /></Form.Item> },
                    { title: 'H (cm)', render: (_, field) => <Form.Item {...field} name={[field.name, 'height']} noStyle><InputNumber min={1} /></Form.Item> },
                    { title: 'Qty', render: (_, field) => <Form.Item {...field} name={[field.name, 'noofBox']} noStyle><InputNumber min={1} /></Form.Item> },
                    {
                      title: '',
                      render: (_, field) => fields.length > 1 ? (
                        <MinusCircleOutlined
                          style={{ color: 'red' }}
                          onClick={() => { remove(field.name); setTimeout(updateTotals, 0); }}
                        />
                      ) : null
                    }
                  ]}
                  footer={() => (
                    <Button
                      type="dashed"
                      onClick={() => add({ length: 30, breadth: 30, height: 30, noofBox: 1 })}
                      block
                      icon={<PlusCircleOutlined />}
                    >
                      Add Box Type
                    </Button>
                  )}
                />
              )}
            </Form.List>
          </Card>

          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            block
            size="large"
            style={{ marginTop: 24 }}
          >
            Create Booking
          </Button>
        </Form>
      </Card>

      {/* History Modal */}
      {/* History Modal */}
      <Modal
        open={listModal}
        title="Booking History"
        onCancel={() => {
          setListModal(false);
          setSearchText(''); // Clear search on close
        }}
        footer={null}
        width={900}
      >
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search by Docket Number or Consignee..."
            allowClear
            enterButton="Search"
            size="large"
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
          />
        </div>

        <Table
          loading={listLoading}
          dataSource={filteredList} // Use the filtered data here
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: 'Docket No',
              dataIndex: 'docketNumber',
              width: 140,
              render: (text) => <strong>{text}</strong>
            },
            {
              title: 'Consignee',
              render: r => r.consigneeid?.companyName || 'N/A'
            },
            {
              title: 'Action',
              width: isSuperAdmin() ? 120 : 80,
              render: (_, r) => (
                <Space>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<EyeOutlined />}
                    onClick={() => handleShowBarcodeFromList(r)}
                  />
                  {isSuperAdmin() && (
                    <Button
                      type="primary"
                      danger
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteBooking(r)}
                    />
                  )}
                </Space>
              )
            }
          ]}
        />
      </Modal>

      {/* Barcode Modal – Download PDF only */}
      <Modal
        open={barcodeModal}
        title={null}
        onCancel={() => setBarcodeModal(false)}
        footer={null}
        width={520}
        centered
        styles={{
          mask: { backgroundColor: 'rgba(0,0,0,0.75)' },
          content: {
            background: '#0f172a',
            borderRadius: 12,
            border: '1px solid #334155',
            boxShadow: '0 20px 50px -12px rgba(0,0,0,0.6)'
          },
          body: { padding: 0 }
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            background: '#1e293b',
            borderBottom: '1px solid #334155',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12
          }}
        >
          <div style={{ color: '#e2e8f0', fontWeight: 600 }}>
            Barcodes – {selectedBooking?.docketNumber || '—'}
          </div>

          <Space size="small">
            <Button
              size="small"
              onClick={() => setBarcodeModal(false)}
              style={{ background: '#334155', border: 'none', color: '#cbd5e1' }}
            >
              Close
            </Button>

            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
              disabled={!selectedBooking?.generatedCodes?.length}
              style={{
                background: '#3b82f6',
                border: 'none',
                color: 'white',
                fontWeight: 500
              }}
            >
              Download PDF
            </Button>
          </Space>
        </div>

        {/* Labels preview */}
        <div
          ref={printLabelsRef}
          style={{
            padding: '20px',
            background: '#0f172a',
            maxHeight: '72vh',
            overflowY: 'auto'
          }}
        >
          {selectedBooking?.generatedCodes?.map((code, i) => (
            <div
              id={`label-${i}`}
              key={i}
              style={{
                background: '#ffffff',
                borderRadius: 8,
                padding: '0',
                marginBottom: 24,
                color: '#000000',
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                width: '450px',
                margin: '0 auto 24px auto',
                overflow: 'hidden',
                fontFamily: 'Arial, sans-serif',
                border: '1px solid #e2e8f0'
              }}
            >
              {/* Top Main Barcode */}
              <div style={{ padding: '20px 20px 0 20px', textAlign: 'center' }}>
                <BarcodeItem value={code} />
              </div>

              <div style={{ padding: '20px', display: 'flex', gap: '20px' }}>
                {/* Left Column */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  <div style={{ marginBottom: '10px' }}>
                    {/* Secondary barcode removed as per user request to only show one barcode */}
                  </div>
                  <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                    <span style={{ color: '#666' }}>CONSIGNEE NAME:</span><br/>
                    <strong>{selectedBooking?.consigneeid?.companyName || selectedBooking?.consigneeid?.name || selectedBooking?.consigneeName || '—'}</strong>
                  </div>
                </div>

                {/* Right Column */}
                <div style={{ flex: 1, fontSize: '13px', lineHeight: '1.8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#666' }}>Docket No:</span>
                    <strong style={{ fontSize: '16px' }}>{selectedBooking?.docketNumber}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>FROM:</span>
                    <strong>
                      {selectedBooking?.pickrequestid?.location ||
                        selectedBooking?.pickrequestid?.locationid?.areaname ||
                        selectedBooking?.pickrequestid?.clientid?.station ||
                        selectedBooking?.pickrequestid?.clientid?.branch?.branchname ||
                        (selectedBooking?.fromLocation && selectedBooking?.fromLocation !== 'N/A' ? selectedBooking?.fromLocation?.split(' (')[0] : null) ||
                        '—'}
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>TO:</span>
                    <strong>{selectedBooking?.locationid?.areaname || selectedBooking?.topincode?.locationname || selectedBooking?.topincode?.pincode || selectedBooking?.toLocation?.split(' (')[0] || '—'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>BKG DT:</span>
                    <strong>{dayjs(selectedBooking?.bookingdate || selectedBooking?.createdAt || new Date()).format('DD/MM/YYYY HH:mm')}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>PKGS:</span>
                    <strong>{i + 1}/{selectedBooking.noofbox}</strong>
                  </div>
                </div>
              </div>

              {/* Bottom Orange Bar */}
              <div style={{
                background: '#3b82f6',
                padding: '12px 16px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                {/* <div style={{
                  width: '24px',
                  height: '24px',
                  background: 'white',
                  color: '#0040ffff',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontStyle: 'italic',
                  fontSize: '16px'
                }}></div> */}
              {/* <strong style={{ fontSize: '20px', letterSpacing: '1px' }}>Spark Logistics</strong> */}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Booking;